import {
  compatiblePhases,
  getDCStringVoltage,
  getMaxCableCurrent,
  getSystemVoltageAndCurrent,
  getThinnestCompatibleCableThickness,
} from 'elements/electrical/helpers'
import {
  getConfiguredOrDefaultDCCableLengthMeters,
  getDefaultDCCableLengthMeters,
} from 'elements/electrical/dcCableLengthDefaults'
import {
  computeDCCablePurchasePlan,
  parseCableLengthOptionsFromFilterSchema,
  pickSmallestFilterOptionAtLeast,
} from 'elements/electrical/dcCableReelLength'
import { getInverterDCCableSegments, getTotalDCCableLengthForInverter } from 'elements/electrical/electricalDCCable'
import { getMinimumACIsolatorCurrentRatingForSlot } from 'elements/electrical/isolatorSelection'
import {
  AC_VOLTAGE_RATING_BUCKET_FILTER_KEY_V2,
  getDefaultVoltageFilterValue,
} from 'elements/hardwareFilter/acVoltageRating/constants'
import { FilterValuesType } from 'elements/hardwareFilter/typeV2'
import { getFiltersSchema } from 'elements/hardwareSelector/Filters'
import { StudioSystemType } from 'types/global'
import type { ComponentTypesV2 } from 'types/selectComponent'
import { FilterOverridesType, SelectComponentFilters } from 'types/selectComponent'
import { SlotType } from 'types/slots'

// Prefer this US cable if thickness allows
const PREFERRED_US_CABLE_CODE = '01-PV-1019X-SJ-BK-2N'
function getPreferredUSCableThickness(): number | undefined {
  try {
    const others = window.AccountHelper.getComponentOtherSpecsAvailable?.()
    const match = others?.find?.((c) => c?.code === PREFERRED_US_CABLE_CODE)
    const thickness = match?.cable_thickness
    return typeof thickness === 'number' ? thickness : undefined
  } catch (_) {
    return undefined
  }
}

const getRequiredACIsolatorVoltage = (phaseType?: string): number | undefined => {
  if (!phaseType) return
  if (phaseType === 'single_phase') return 240
  if (phaseType === 'three_phase' || phaseType === 'two_phase') return 415
  return
}

function getRequiredDCCableThicknessForSlot(
  slot: SlotType,
  supplyPhase: compatiblePhases
): { requiredThickness: number; context: string } {
  const attachedObject = window.editor.objectByUuid(slot.attachToUuid)

  if (attachedObject?.type !== 'OsInverter') {
    const safeLength = getConfiguredOrDefaultDCCableLengthMeters(slot?.properties?.cableLength, attachedObject)
    const current = getMaxCableCurrent(attachedObject)
    const dcVoltage = getDCStringVoltage(attachedObject)
    const requiredThickness = getThinnestCompatibleCableThickness(safeLength, supplyPhase, current, 1, dcVoltage)
    return { requiredThickness, context: attachedObject?.type || 'unknown' }
  }

  const segments = getInverterDCCableSegments(attachedObject.getSystem?.(), attachedObject)
  if (!segments.length) {
    const firstMppt = attachedObject.mppts?.()?.[0]
    const fallbackLength = getDefaultDCCableLengthMeters(firstMppt)
    const current = getMaxCableCurrent(firstMppt || attachedObject)
    const dcVoltage = getDCStringVoltage(firstMppt || attachedObject)
    return {
      requiredThickness: getThinnestCompatibleCableThickness(fallbackLength, supplyPhase, current, 1, dcVoltage),
      context: 'inverter-fallback',
    }
  }

  const maxRequiredThickness = segments.reduce((maxThickness, segment) => {
    const current = getMaxCableCurrent(segment.location)
    const dcVoltage = getDCStringVoltage(segment.location)
    const required = getThinnestCompatibleCableThickness(segment.length, supplyPhase, current, 1, dcVoltage)
    return Math.max(maxThickness, required)
  }, 0)

  return {
    requiredThickness: maxRequiredThickness,
    context: 'inverter-segments',
  }
}

export function getSlotKey(slot: SlotType): string {
  if (slot.attachToUuid) {
    return `${slot.type}_${slot.attachToUuid}`
  } else {
    return slot.type
  }
}

export function getSlotRestriction(slot: SlotType): boolean {
  const restrictToOne = true
  return restrictToOne
}

export function getSlotTitle(slot: SlotType): string {
  var title = ''
  if (slot.type === 'dc_isolator') {
    title = 'DC Isolator'
  } else if (slot.type === 'ac_isolator') {
    title = 'AC Isolator'
  } else if (slot.type === 'dc_cable') {
    title = 'DC Cabling'
  } else if (slot.type === 'ac_cable') {
    title = 'AC Cabling'
  } else if (slot.type === 'trunk_cable') {
    title = 'Trunk Cabling'
  } else if (slot.type === 'dc_optimizer') {
    title = 'DC Optimizers'
  }
  return title
}

export const initialFilterSettingsForSlot = (
  slot: SlotType,
  supplyPhase: compatiblePhases,
  orgCountry?: string
): SelectComponentFilters => {
  var filterSettings = {}
  const filterOverrides = filterOverridesForSlot(slot, supplyPhase, orgCountry)
  filterOverrides.forEach((override) => {
    filterSettings[override.key] = override.value
  })
  return filterSettings
}

export const filterOverridesForSlot = (
  slot: SlotType,
  supplyPhase: compatiblePhases,
  orgCountry?: string
): FilterOverridesType => {
  var filterOverrides: FilterOverridesType = []
  const slotKey = getSlotKey(slot)

  var componentType = OsSystem.getComponentTypeFromSlotKey(slotKey)
  if (componentType) filterOverrides.push({ key: 'other_component_type', value: componentType })

  if (slot.type.includes('ac_')) filterOverrides.push({ key: 'current_type', value: 'ac' })
  if (slot.type.includes('dc_')) filterOverrides.push({ key: 'current_type', value: 'dc' })

  if (slot.type === 'ac_isolator') {
    const isolatorLocation = window.editor.objectByUuid(slot.attachToUuid)
    const inverter = isolatorLocation.type === 'OsMppt' ? isolatorLocation.parent : isolatorLocation
    const minimumCurrent = getMinimumACIsolatorCurrentRatingForSlot(slot)
    if (minimumCurrent != null) {
      filterOverrides.push({
        key: 'current_rating_range',
        value: `${minimumCurrent},1000`,
        options: [{ title: `${minimumCurrent}A <`, value: `${minimumCurrent},1000` }],
      })
    }
    if (inverter.phase_type) filterOverrides.push({ key: 'phase_type', value: inverter.phase_type })
    const voltageFilterValue = getDefaultVoltageFilterValue(inverter?.phase_type)
    if (voltageFilterValue !== 'all') {
      filterOverrides.push({
        key: AC_VOLTAGE_RATING_BUCKET_FILTER_KEY_V2,
        value: voltageFilterValue,
      })
    }
  }

  if (slot.type === 'dc_isolator') {
    const strings =
      window.editor.objectByUuid(slot.attachToUuid).type === 'OsString'
        ? [window.editor.objectByUuid(slot.attachToUuid)]
        : window.editor.objectByUuid(slot.attachToUuid).children
    const { voltage, current } = getSystemVoltageAndCurrent(strings)
    filterOverrides.push({
      key: 'voltage_to_current_rating',
      value: voltage * 1.25 + ',' + current * 1.25,
      options: [{ title: 'Compatible with system', value: voltage * 1.25 + ',' + current * 1.25 }],
    })
  }

  if (slot.type === 'dc_cable') {
    const attachedObject = window.editor.objectByUuid(slot.attachToUuid)
    const current = getMaxCableCurrent(attachedObject)
    const filtersSchema = getFiltersSchema('other')
    const componentRollLengthMeters =
      attachedObject?.type === 'OsInverter'
        ? computeDCCablePurchasePlan(
            getTotalDCCableLengthForInverter(attachedObject.getSystem?.(), attachedObject),
            parseCableLengthOptionsFromFilterSchema(filtersSchema)
          ).reelLength
        : orgCountry === 'US'
          ? 152.4
          : 100
    const dcVoltage = getDCStringVoltage(attachedObject)
    const { requiredThickness: cableThickness } = getRequiredDCCableThicknessForSlot(slot, supplyPhase)
    const cableThicknessFilter = filtersSchema.filter((filter) => filter.key === 'cable_thickness')[0]
    // assumes cable thicknesses are ordered lowest to highest
    const bestCableThicknessOption = cableThicknessFilter.options.find(
      (option) => Number(option.value) >= cableThickness
    )
    if (bestCableThicknessOption) {
      const allowedThicknesses =
        cableThicknessFilter.options
          .filter((option) => Number(option.value) >= Number(bestCableThicknessOption.value))
          .map((option) => option.value) || []
      // Inverter-level auto-select may upsize for voltage drop; fetch all thicknesses ≥ minimum.
      if (attachedObject?.type === 'OsInverter' && allowedThicknesses.length > 1) {
        filterOverrides.push({
          key: 'cable_thickness__in',
          value: allowedThicknesses.join(','),
        })
      } else {
        filterOverrides.push({
          key: 'cable_thickness',
          value: bestCableThicknessOption.value,
        })
      }
    }

    let appliedLengthFilter = false

    if (orgCountry === 'US') {
      // If preferred US cable supports at least the required thickness, set codes to that SKU
      const requiredThickness = Number(bestCableThicknessOption?.value)
      const preferredThickness = getPreferredUSCableThickness()
      if (!isNaN(requiredThickness) && preferredThickness && requiredThickness === preferredThickness) {
        filterOverrides.push({ key: 'codes', value: PREFERRED_US_CABLE_CODE })
      } else {
        const cableLengthFilter = filtersSchema.filter((filter) => filter.key === 'cable_length')[0]
        const bestCableLengthOption = pickSmallestFilterOptionAtLeast(
          cableLengthFilter?.options,
          componentRollLengthMeters
        )
        if (bestCableLengthOption) {
          filterOverrides.push({ key: 'cable_length', value: bestCableLengthOption.value })
          appliedLengthFilter = true
        }
      }
    }

    // Non-US ensure cable_length is set
    if (!appliedLengthFilter && orgCountry !== 'US') {
      const cableLengthFilter = filtersSchema.filter((filter) => filter.key === 'cable_length')[0]
      const bestCableLengthOption = pickSmallestFilterOptionAtLeast(
        cableLengthFilter?.options,
        componentRollLengthMeters
      )
      if (bestCableLengthOption) {
        filterOverrides.push({ key: 'cable_length', value: bestCableLengthOption.value })
      }
    }
  }
  return filterOverrides
}

export const getComponentTypeV2BySlot = (slot: SlotType): ComponentTypesV2 | undefined => {
  const slotKey = getSlotKey(slot)

  var componentType = OsSystem.getComponentTypeFromSlotKey(slotKey)
  if (componentType) {
    return componentType
  }
}

export const getDefaultFilterValuesBySlot = (
  slot: SlotType,
  supplyPhase: compatiblePhases,
  orgCountry?: string
): FilterValuesType => {
  const defaultValues: FilterValuesType = {}

  if (slot.type.includes('ac_')) defaultValues['current_type'] = 'ac'
  if (slot.type.includes('dc_')) defaultValues['current_type'] = 'dc'

  if (slot.type === 'ac_isolator') {
    const isolatorLocation = window.editor.objectByUuid(slot.attachToUuid)
    const inverter = isolatorLocation.type === 'OsMppt' ? isolatorLocation.parent : isolatorLocation
    const minimumCurrent = getMinimumACIsolatorCurrentRatingForSlot(slot)

    if (minimumCurrent != null) {
      defaultValues['current_rating__gte'] = minimumCurrent
      defaultValues['current_rating__lte'] = 1000
    }
    if (inverter.phase_type) defaultValues['phase_type'] = inverter.phase_type
    const voltageFilterValue = getDefaultVoltageFilterValue(inverter?.phase_type)
    if (voltageFilterValue !== 'all') {
      defaultValues[AC_VOLTAGE_RATING_BUCKET_FILTER_KEY_V2] = voltageFilterValue
    }
  }

  if (slot.type === 'dc_isolator') {
    const strings =
      window.editor.objectByUuid(slot.attachToUuid).type === 'OsString'
        ? [window.editor.objectByUuid(slot.attachToUuid)]
        : window.editor.objectByUuid(slot.attachToUuid).children
    const { voltage, current } = getSystemVoltageAndCurrent(strings)
    defaultValues['voltage_to_current_rating'] = voltage * 1.25 + ',' + current * 1.25
  }

  if (slot.type === 'dc_cable') {
    const attachedObject = window.editor.objectByUuid(slot.attachToUuid)
    const current = getMaxCableCurrent(attachedObject)
    const filtersSchema = getFiltersSchema('other') || []
    const componentRollLengthMeters =
      attachedObject?.type === 'OsInverter'
        ? computeDCCablePurchasePlan(
            getTotalDCCableLengthForInverter(attachedObject.getSystem?.(), attachedObject),
            parseCableLengthOptionsFromFilterSchema(filtersSchema)
          ).reelLength
        : orgCountry === 'US'
          ? 152.4
          : 100
    const dcVoltage = getDCStringVoltage(attachedObject)
    const { requiredThickness: cableThickness, context } = getRequiredDCCableThicknessForSlot(slot, supplyPhase)
    const cableThicknessFilter = filtersSchema.filter((filter) => filter.key === 'cable_thickness')[0]
    // assumes cable thicknesses are ordered lowest to highest
    const bestCableThicknessOption = cableThicknessFilter?.options?.find(
      (option) => Number(option.value) >= cableThickness
    )
    if (bestCableThicknessOption) {
      const allowedThicknesses =
        cableThicknessFilter?.options
          ?.filter((option) => Number(option.value) >= Number(bestCableThicknessOption.value))
          .map((option) => option.value) || []
      defaultValues['cable_thickness__in'] = allowedThicknesses.join(',')
    }

    // Prefer US code if its thickness supports the requirement; else set cable length
    if (orgCountry === 'US') {
      const requiredThickness = Number(bestCableThicknessOption?.value)
      const preferredThickness = getPreferredUSCableThickness()
      if (!isNaN(requiredThickness) && preferredThickness && requiredThickness === preferredThickness) {
        defaultValues['codes'] = PREFERRED_US_CABLE_CODE
      } else {
        const cableLengthFilter = filtersSchema.filter((filter) => filter.key === 'cable_length')[0]
        const bestCableLengthOption = pickSmallestFilterOptionAtLeast(
          cableLengthFilter?.options,
          componentRollLengthMeters
        )
        if (bestCableLengthOption) {
          defaultValues['cable_length'] = bestCableLengthOption.value
        }
      }
    } else {
      const cableLengthFilter = filtersSchema.filter((filter) => filter.key === 'cable_length')[0]
      const bestCableLengthOption = pickSmallestFilterOptionAtLeast(
        cableLengthFilter?.options,
        componentRollLengthMeters
      )
      if (bestCableLengthOption) {
        defaultValues['cable_length'] = bestCableLengthOption.value
      }
    }

  }
  return defaultValues
}

export function getSlotsByLocation(uuid: string, system: StudioSystemType): SlotType[] {
  var slots: SlotType[] = []
  if (system.userData?.slots) slots.push(...system.userData.slots.filter((slot) => slot.attachToUuid === uuid))
  return slots
}

export function updateSlot(slotToUpdate: SlotType, system: StudioSystemType): any {
  if (!system.userData.slots) {
    // error
    return
  }
  var slotsToSave = system.userData.slots
  const slotIndex = slotsToSave.findIndex(
    (slot) => slot.attachToUuid === slotToUpdate.attachToUuid && slot.type === slotToUpdate.type
  )
  if (slotIndex === -1) {
    //error
    return
  }
  slotsToSave[slotIndex] = slotToUpdate
  window.editor.execute(new window.SetValueCommand(system, 'slots', slotsToSave))
}
